<template>
  <WCForm
    :form="$v.form"
    @update:value="$emit('update:value', form)"
    @update:valid="$emit('update:valid', $event)"
  >
    <WCGiftCardDesignSelector :designs="designs" v-model="form.design" />

    <div class="row">
      <div class="col-12 mb-2">
        <WCGiftCardAmountSelector
          v-if="giftCard.egiftCardAmounts"
          :giftCard="giftCard"
          v-model="form.amount"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6">
        <WCFormGroup :field="$v.form.recipientName">
          <WCLabel for="gift-card__recipient-name">{{ $t('recipientName') }}</WCLabel>
          <WCInput class="bg-light" id="gift-card__recipient-name" />
          <WCValidationErrors />
        </WCFormGroup>

        <WCFormGroup :field="$v.form.senderName">
          <WCLabel for="gift-card__sender-name">{{ $t('senderName') }}</WCLabel>
          <WCInput class="bg-light" id="gift-card__sender-name" />
          <WCValidationErrors />
        </WCFormGroup>
      </div>

      <div class="col-sm-6">
        <WCFormGroup :field="$v.form.message">
          <WCLabel for="message">{{ $t('message') }}</WCLabel>
          <WCTextArea :placeholder="$t('eGiftCardFormMessage')" id="message" rows="4" />
          <WCValidationErrors />
        </WCFormGroup>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-sm-12">
        <div class="row">
          <fieldset class="col-12 col-sm-6">
            <legend class="h4">
              {{ $t('pleaseSelectAtLeastOneDelivery') }}:<span class="text-danger pl-1">*</span>
            </legend>

            <div>
              <WCFormGroup>
                <WCCheckbox
                  id="giftCard-form-email__checkbox"
                  value="Email"
                  v-model="form.deliveryMethod"
                >
                  {{ $t('email') }}
                </WCCheckbox>
              </WCFormGroup>

              <WCFormGroup
                v-if="form.deliveryMethod.includes('Email')"
                :field="$v.form.recipientEmail"
              >
                <WCLabel for="giftCard-form__email">{{ $t('recipientsEmail') }}</WCLabel>
                <WCInput class="bg-light" id="giftCard-form__email" />
                <WCValidationErrors />
              </WCFormGroup>
            </div>

            <div>
              <WCFormGroup>
                <WCCheckbox value="Text" v-model="form.deliveryMethod">
                  {{ $t('text') }}
                </WCCheckbox>
              </WCFormGroup>
              <WCFormGroup
                v-if="form.deliveryMethod.includes('Text')"
                :field="$v.form.recipientPhoneNumber"
              >
                <WCLabel for="giftCard-form__number">{{ $t('recipientsPhone') }}</WCLabel>
                <WCInput
                  class="bg-light"
                  id="giftCard-form__number"
                  @input="changePhoneNumberFormat(form.recipientPhoneNumber)"
                />
                <WCValidationErrors />
              </WCFormGroup>
            </div>
            <WCFormGroup>
              <WCCheckbox value="Print" v-model="form.deliveryMethod">
                {{ $t('print') }}
              </WCCheckbox>
            </WCFormGroup>
          </fieldset>

          <fieldset
            class="col-xs-12 col-sm-6"
            v-if="form.deliveryMethod.includes('Email') || form.deliveryMethod.includes('Text')"
          >
            <legend class="h4">
              {{ $t('pleaseSelectDelDateTime') }}:
              <span class="text-danger pl-1">*</span>
            </legend>

            <WCRadioButtonGroup v-model="form.selected">
              <WCRadioButton
                class="align-items-center"
                name="deliveryDate"
                id="sendNow"
                v-model="form.selected"
              >
                {{ $t('sendNow') }}
              </WCRadioButton>

              <WCRadioButton
                class="align-items-center"
                name="deliveryDate"
                id="sendLater"
                v-model="form.selected"
              >
                {{ $t('sendLater') }}
              </WCRadioButton>
            </WCRadioButtonGroup>

            <!-- START : Send Later Form Group -->
            <WCFormGroup v-if="form.selected === 'sendLater'">
              <WCLabel for="giftCard-form__datePicker" :required="true">
                {{ $t('sendDate') }}
              </WCLabel>
              <WCDateTimePicker
                datePlaceholder=" "
                timePlaceholder=" "
                dateInputId="giftCard-form__datePicker"
                timeInputId="giftCard-form__timePicker"
                :dateFormat="datePickerFormat"
                :timeFormat="timePickerFormat"
                :initialValue="initialSendLaterDate"
                :minDate="sendLaterMinDate"
                :maxDate="sendLaterMaxDate"
                :mode="datePickerMode"
                @onChange="onSendLaterDateChange"
                @onValidation="onSendLaterValidation"
                ref="sendLaterDatePicker"
                :datePickerAriaLabel="$t('sendLaterDatePicker')"
                :timePickerAriaLabel="$t('sendLaterTimePicker')"
              />
              <WCValidationErrors :validation="sendDateErrorObj" class="pt-1 pl-1" />
            </WCFormGroup>
            <!-- END : Send Later Form Group -->
          </fieldset>
        </div>
      </div>
    </div>
  </WCForm>
</template>
<script>
import WCFormGroup from '@/modules/forms/components/WCFormGroup/WCFormGroup.vue';
import WCInput from '@/modules/forms/components/WCInput/WCInput.vue';
import WCRadioButton from '@/modules/forms/components/WCRadioButton/WCRadioButton.vue';
import WCRadioButtonGroup from '@/modules/forms/components/WCRadioButtonGroup/WCRadioButtonGroup.vue';
import WCForm from '@/modules/forms/components/WCForm/WCForm.vue';
import WCTextArea from '@/modules/forms/components/WCTextArea/WCTextArea.vue';
import WCValidationErrors from '@/modules/forms/components/WCValidationErrors/WCValidationErrors.vue';
import WCLabel from '@/modules/forms/components/WCLabel/WCLabel.vue';
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import { required, maxLength, email } from 'vuelidate/lib/validators';
import { phone, minDate, maxDate } from '@/utils/validators/validators';
import { FORMAT, MODE } from '@/constants/DatePickerConstants';
import { addDays, addHours, addYears, endOfDay, startOfDay } from 'date-fns';
import WCDateTimePicker from '@/components/WCDateTimePicker/WCDateTimePicker.vue';
import PhoneNumberFormatMixin from '@/mixins/PhoneNumberFormatMixin';
import WCGiftCardAmountSelector from '../WCGiftCardAmountSelector/WCGiftCardAmountSelector.vue';
import WCGiftCardDesignSelector from '../WCGiftCardDesignSelector/WCGiftCardDesignSelector.vue';

export default {
  name: 'WCGiftCardBuilderForm',
  components: {
    WCInput,
    WCFormGroup,
    WCLabel,
    WCForm,
    WCTextArea,
    WCCheckbox,
    WCValidationErrors,
    WCRadioButton,
    WCRadioButtonGroup,
    WCGiftCardAmountSelector,
    WCGiftCardDesignSelector,
    WCDateTimePicker,
  },
  mixins: [PhoneNumberFormatMixin],
  data() {
    return {
      form: {
        selected: 'sendNow',
        recipientPhoneNumber: null,
        recipientEmail: null,
        recipientName: '',
        senderName: '',
        message: '',
        deliveryMethod: [],
        sendDate: null,
        amount: '',
        design: null,
      },
      sendDateErrorObj: {},
    };
  },
  validations() {
    const recipientEmail = {
      required,
      email,
      maxLength: maxLength(50),
    };
    const recipientPhoneNumber = {
      required,
      phone,
    };

    const sendDate = {
      required,
      minDate: minDate(this.sendLaterMinDate),
      maxDate: maxDate(this.sendLaterMaxDate),
    };

    const amount = {
      required,
      isValid: value =>
        (value && value >= this.giftCard.minCardValue && value <= this.giftCard.maxCardValue) ||
        (!this.giftCard.maxCardValue && value !== '' && value >= this.giftCard.minCardValue),
    };

    return {
      form: {
        design: {
          required,
        },
        recipientName: {
          required,
          maxLength: maxLength(25),
        },
        senderName: {
          required,
          maxLength: maxLength(25),
        },
        message: {
          maxLength: maxLength(250),
        },
        deliveryMethod: {
          required,
        },
        sendDate: this.form.selected === 'sendLater' ? sendDate : {},
        recipientEmail: this.form.deliveryMethod.includes('Email') ? recipientEmail : {},
        recipientPhoneNumber: this.form.deliveryMethod.includes('Text') ? recipientPhoneNumber : {},
        amount:
          (this.form.amount && this.form.amount !== '') || this.form.amount === 0 ? amount : {},
      },
    };
  },
  computed: {
    designs() {
      return this.$configuration.giftCardProfile.designs;
    },
    giftCard() {
      return this.$configuration.giftCardProfile;
    },
    currentDate() {
      return new Date();
    },
    /**
     * To get send later initial date
     */
    initialSendLaterDate() {
      return addHours(startOfDay(addDays(new Date(), 1)), 12);
    },
    /**
     * To get send later date picker format
     */
    datePickerFormat() {
      return FORMAT.DATE_DEFAULT;
    },
    /**
     * To get send later time picker format
     */
    timePickerFormat() {
      return FORMAT.TIME_DEFAULT;
    },
    /**
     * To get send later min date
     */
    sendLaterMinDate() {
      return new Date();
    },
    /**
     * To get send later max date
     */
    sendLaterMaxDate() {
      return endOfDay(addYears(new Date(), 2));
    },
    /**
     * To get send later date picker mode to show both date & time picker
     */
    datePickerMode() {
      return MODE.DATE_TIME;
    },
  },
  methods: {
    amountChanged(amount) {
      this.form.amount = amount;
      this.$emit('update:valid', !this.$v.$invalid);
    },
    /**
     * Method to set send date on change
     */
    onSendLaterDateChange(event) {
      const { date } = event;
      this.form.sendDate = date;
    },
    /**
     * Method to get send date validation
     */
    onSendLaterValidation(event) {
      this.sendDateErrorObj = event;
      this.$v.form.sendDate.$touch();
    },
    /**
     * Method to format the phone number
     * Uses PhoneNumberFormatMixin
     */
    changePhoneNumberFormat(phoneNumber) {
      this.form.recipientPhoneNumber = this.formatPhoneNumber(phoneNumber);
    },
  },
};
</script>
